/**
 * Permit Wall ticker (DISPLAY-only office TV).
 *
 * A continuous right-to-left marquee of the transactions changed inside
 * the recent working-day window (excluding the ones already shown up
 * top). The motion is a single CSS transform animation on a belt of N
 * identical copies ("groups") of the list: GPU-composited, no layout
 * properties animated, no per-frame JavaScript, and no DOM growth, so it
 * is safe to run 24/7 on an unattended screen.
 *
 * Why N copies (not a fixed doubled track): a 2-copy track only loops
 * gap-free when one copy is at least as wide as the screen. With only a
 * few recent transactions the content is narrower than the TV, so a fixed
 * -50% translate would scroll the items off and leave the belt blank for
 * most of the cycle. We measure one group against the container and repeat
 * it enough times that the belt always overflows the viewport by at least
 * one group, then translate by exactly one group width so the loop stays
 * seamless and the belt is never empty.
 *
 * Speed is a constant pixel rate (news-ticker feel), so the duration is
 * derived from the measured group width rather than fixed. React Query's
 * structural sharing keeps the row array stable across polls when nothing
 * changed, so the belt does not re-measure or jump in steady state.
 */

import { useLayoutEffect, useRef, useState, type CSSProperties } from "react";
import { pillFor, WALL_EVENT_COLORS, type WallRow, type WallTheme } from "@/lib/wall";

/** Constant scroll speed in px/s: brisk like a TV news ticker, still
 *  readable on a wall screen from across the room. Tune here. */
const TICKER_PX_PER_SEC = 130;

function TickerItem({ row, theme }: { row: WallRow; theme: WallTheme }) {
  const pill = pillFor(theme, row.requestStatus);
  const updateColor = row.event ? WALL_EVENT_COLORS[theme][row.event].text : "var(--text-dim)";
  return (
    <div
      dir="rtl"
      style={{
        display: "flex",
        alignItems: "center",
        gap: "0.9vw",
        flex: "none",
        padding: "0 1.9vw",
        borderLeft: "1px solid var(--ticker-item-border)",
      }}
    >
      <span style={{ fontSize: "1.55vw", fontWeight: 800, whiteSpace: "nowrap" }}>{row.name}</span>
      <span
        dir="ltr"
        style={{
          unicodeBidi: "isolate",
          fontSize: "1.22vw",
          fontWeight: 700,
          color: "var(--text-dim)",
          fontVariantNumeric: "tabular-nums",
          whiteSpace: "nowrap",
        }}
      >
        {row.number}
      </span>
      <span style={{ fontSize: "1.32vw", fontWeight: 600, color: updateColor, whiteSpace: "nowrap" }}>
        {row.update}
      </span>
      {row.requestStatus ? (
        <span
          style={{
            display: "inline-flex",
            alignItems: "center",
            gap: "0.4vw",
            padding: "0.45vh 0.85vw",
            borderRadius: "0.6vw",
            fontSize: "1.02vw",
            fontWeight: 800,
            background: pill.bg,
            color: pill.fg,
            whiteSpace: "nowrap",
          }}
        >
          <span style={{ width: "0.6vw", height: "0.6vw", borderRadius: "50%", background: pill.dot, flex: "none" }} />
          {row.requestStatus}
        </span>
      ) : null}
    </div>
  );
}

export function WallTicker({ rows, theme, windowDays }: { rows: WallRow[]; theme: WallTheme; windowDays: number }) {
  const containerRef = useRef<HTMLDivElement>(null);
  const groupRef = useRef<HTMLDivElement>(null);
  const [copies, setCopies] = useState(2);
  const [shiftPx, setShiftPx] = useState(0);

  // Measure one group against the visible container and repeat it enough
  // times to always overflow by at least one group. useLayoutEffect runs
  // before paint, so the user never sees the pre-measurement frame. A
  // ResizeObserver re-measures on TV-resolution changes; the `rows`
  // dependency re-measures only when the data actually changed (stable
  // reference otherwise, thanks to React Query structural sharing).
  useLayoutEffect(() => {
    const measure = () => {
      const group = groupRef.current;
      const container = containerRef.current;
      if (!group || !container) return;
      const groupW = group.offsetWidth;
      const contW = container.offsetWidth;
      if (groupW <= 0) return;
      setCopies(Math.max(2, Math.ceil(contW / groupW) + 1));
      setShiftPx(groupW);
    };
    measure();
    const ro = new ResizeObserver(measure);
    if (containerRef.current) ro.observe(containerRef.current);
    if (groupRef.current) ro.observe(groupRef.current);
    return () => ro.disconnect();
  }, [rows]);

  const durationS = shiftPx > 0 ? shiftPx / TICKER_PX_PER_SEC : 0;
  const trackStyle: CSSProperties = {
    "--wall-belt-shift": `${shiftPx}px`,
    animationDuration: durationS ? `${durationS}s` : undefined,
    // Hold still until the first measurement lands so we never animate the
    // 50% fallback at the wrong speed for a frame.
    animationPlayState: shiftPx > 0 ? "running" : "paused",
  } as CSSProperties;

  return (
    <div
      style={{
        flex: "none",
        height: "13.5vh",
        position: "relative",
        borderTop: "1px solid var(--divider)",
        background: "var(--ticker-bg)",
        display: "flex",
        alignItems: "center",
        overflow: "hidden",
      }}
    >
      <div
        ref={containerRef}
        dir="ltr"
        style={{
          position: "absolute",
          inset: 0,
          display: "flex",
          alignItems: "center",
          overflow: "hidden",
          WebkitMaskImage: "linear-gradient(90deg,transparent,#000 5%,#000 86%,transparent)",
          maskImage: "linear-gradient(90deg,transparent,#000 5%,#000 86%,transparent)",
        }}
      >
        {rows.length === 0 ? (
          <div dir="rtl" style={{ width: "100%", textAlign: "center", color: "var(--text-dim)", fontSize: "1.3vw", fontWeight: 700 }}>
            لا توجد تحديثات في آخر {windowDays} أيام عمل
          </div>
        ) : (
          <div className="wall-ticker-track" style={trackStyle}>
            {Array.from({ length: copies }).map((_, ci) => (
              <div
                key={ci}
                ref={ci === 0 ? groupRef : undefined}
                className="wall-ticker-group"
                aria-hidden={ci > 0 ? true : undefined}
              >
                {rows.map((r) => (
                  <TickerItem key={r.id} row={r} theme={theme} />
                ))}
              </div>
            ))}
          </div>
        )}
      </div>

      <div
        style={{
          position: "absolute",
          right: 0,
          top: 0,
          bottom: 0,
          display: "flex",
          alignItems: "center",
          gap: "0.65vw",
          padding: "0 1.7vw 0 2.6vw",
          background: "var(--ticker-fade)",
          fontSize: "1.12vw",
          fontWeight: 800,
          color: "var(--text)",
          zIndex: 2,
          whiteSpace: "nowrap",
        }}
      >
        <span
          style={{
            width: "0.72vw",
            height: "0.72vw",
            borderRadius: "50%",
            background: "#f5a55a",
            boxShadow: "0 0 14px #f5a55a",
            animation: "wallLiveDot 1.6s ease-in-out infinite",
          }}
        />
        آخر {windowDays} أيام عمل
      </div>
    </div>
  );
}
